import { EmployeeLineItem } from "@interfaces/employee";
import React, { useEffect, useLayoutEffect, useRef } from "react";
import { CellProps, Column } from "react-datasheet-grid";
import { GroupBase, SelectInstance, components } from "react-select";
import AsyncSelect from "react-select/async";

type Choice = {
  label: string;
  value: string;
};

type SelectOptions = {
  options: Choice[];
  selectedEmployee: (id: string) => EmployeeLineItem | undefined;
  searchEmployees: (query: string) => Choice[];
  updateSocialFactors: (rowIndex: number, employeeId: string) => void;
  disabled?: boolean;
};
const Input = (props: any) => {
  return <components.Input {...props} autoComplete={"employee"} />;
};

export const SearchComponent = ({
  active,
  rowIndex,
  rowData,
  setRowData,
  focus,
  stopEditing,
  columnData,
}: CellProps<string | null, SelectOptions>) => {
  const ref = useRef<SelectInstance<Choice, false, GroupBase<Choice>>>(null);

  const promiseOptions = (inputValue: string) =>
    new Promise<Choice[]>((resolve) => {
      resolve(columnData.searchEmployees(inputValue));
    });

  useLayoutEffect(() => {
    if (focus) {
      ref.current?.focus();
    } else {
      ref.current?.blur();
    }
  }, [focus]);

  return (
    <AsyncSelect
      ref={ref}
      cacheOptions
      components={{ Input }}
      styles={{
        container: (provided) => ({
          ...provided,
          flex: 1,
          alignSelf: "stretch",
          pointerEvents: focus ? undefined : "none",
        }),
        control: (provided) => ({
          ...provided,
          height: "100%",
          border: "none",
          boxShadow: "none",
          background: "none",
        }),
        indicatorSeparator: (provided) => ({
          ...provided,
          opacity: 0,
        }),
        indicatorsContainer: (provided) => ({
          ...provided,
          opacity: active ? 1 : 0,
        }),
        placeholder: (provided) => ({
          ...provided,
          opacity: active ? 1 : 0,
        }),
      }}
      isDisabled={columnData.disabled}
      value={columnData.options.find(({ value }) => value === rowData) ?? null}
      menuPortalTarget={document.body}
      menuIsOpen={focus}
      onChange={(choice) => {
        if (choice === null) return;

        columnData.updateSocialFactors(rowIndex, choice.value);
        setRowData(choice.value);
        setTimeout(stopEditing, 0);
      }}
      onMenuClose={() => stopEditing({ nextRow: false })}
      loadOptions={promiseOptions}
    />
  );
};

export const searchColumn = (
  options: SelectOptions,
  pasteValue: (opt: { rowData: string | null; value: string }) => string | null
): Column<string | null, SelectOptions> => ({
  component: SearchComponent as any,
  columnData: options,
  disableKeys: true,
  keepFocus: true,
  disabled: options.disabled,
  deleteValue: () => null,
  copyValue: ({ rowData }) => {
    if (!rowData) {
      return null;
    }
    return options.options
      .filter((choice) => rowData.includes(choice.value))
      .join(",");
  },
  pasteValue,
});
